/**
 * Created by Atanas Vasilev at avant.vasilev@gmail.com.
 */
// import { Device, DeviceOrientation, LayerManager, MobileView } from '@egt/egt-library'
import { Aspect } from './Aspect'

export type StageConfig = {
    canvasWidth: number
    canvasHeight: number
    stageWidth: number
    stageHeight: number
    visibleWidth: number
    visibleHeight: number
    originalStageWidth?: number
    originalStageHeight?: number
}

export class Stage {
    protected static isMobile: boolean
    protected static TALL_DEVICE_BOTTOM_OFFSET: number = 91

    protected _stage: PIXI.display.Stage | PIXI.Container
    protected _renderer: any
    protected _canvasContainer: HTMLElement
    protected _content: HTMLElement
    protected _options: StageConfig
    protected _windowWidth: number
    protected _windowHeight: number
    protected _isTallDevice: boolean
    protected _additionalHeightOnSmallDevices: number
    protected egtLib: any
    protected visible_aspect: Aspect
    protected stage_aspect: Aspect
    protected _usedSafePadding = 0

    constructor(
        egtLib: any,
        isMobile: boolean,
        stageOptions?: StageConfig,
        isUsingSingleCodeBase?: boolean,
        shouldNotUsePixiResolution?: boolean,
        additionalHeightOnSmallDevices: number = 0
    ) {
        Stage.isMobile = isMobile

        this.egtLib = egtLib
        this._options = {
            canvasWidth: egtLib.Device.isMobile || isUsingSingleCodeBase ? 960 : 1280,
            canvasHeight: egtLib.Device.isMobile || isUsingSingleCodeBase ? 540 : 720,
            stageWidth: egtLib.Device.isMobile ? 960 : 1280,
            stageHeight: egtLib.Device.isMobile ? 540 : 720,
            visibleWidth: egtLib.Device.isMobile ? 960 : 1152,
            visibleHeight: egtLib.Device.isMobile ? 540 : 720,
        }

        if ((window as any).gpts['StageInfo'] !== undefined) {
            this._isTallDevice = (window as any).gpts.StageInfo.isTallDevice
        }

        if (stageOptions) {
            this._options = stageOptions
        }

        this._options.originalStageWidth = this._options.stageWidth
        this._options.originalStageHeight = this._options.stageHeight

        this.stage_aspect = new Aspect(this._options.stageWidth, this._options.stageHeight)
        this.visible_aspect = new Aspect(this._options.visibleWidth, this._options.visibleHeight)

        this._content = document.getElementById('content')
        this._content.style.width = `${this.stageWidth}px`
        this._content.style.height = `${this.stageHeight}px`

        this._additionalHeightOnSmallDevices = additionalHeightOnSmallDevices

        this._canvasContainer = document.getElementById('gpts')

        // Starts rendering canvas.
        if (isMobile) {
            if (PIXI.PRECISION) {
                // not available in 3.0.11 which is used in mobile slot roulette
                PIXI.settings.PRECISION_FRAGMENT = PIXI.PRECISION.HIGH
            }
        }

        const shouldNotChangeResolution = egtLib.Device.isMobile || shouldNotUsePixiResolution || !isUsingSingleCodeBase
        this._renderer = PIXI.autoDetectRenderer(this._options.canvasWidth, this._options.canvasHeight, {
            antialias: true,
            roundPixels: true,
            transparent: true,
            resolution: shouldNotChangeResolution ? 1 : 1.3334,
        })
        this._canvasContainer.append(this._renderer.view)
        this._renderer.backgroundColor = 0x000000

        if (isMobile || isUsingSingleCodeBase) {
            if (!this.egtLib.LayerManager && !this.egtLib.LayerManagerClass) {
                this._stage = new PIXI.Container() // mobile slotroulette case (egtlib doesn't have LayerManager)
            } else {
                this._stage = new PIXI.display.Stage()
                if (this.egtLib.LayerManagerClass) {
                    //this is needed in case the egtlib from mobile roulette overwrites the lib loaded with the platform
                    this.egtLib.LayerManagerClass.Instance.stage = this._stage
                } else {
                    this.egtLib.LayerManager.stage = this._stage
                }
            }
        } else {
            this._stage = new PIXI.Container()
        }

        // Used to be populated through games
        ;(PIXI as any).renderer = this._renderer
        this.animate()

        if (egtLib.Device.isMobile) {
            const disableZoom = document.createElement('div')
            disableZoom.id = 'disableZoom'
            document.body.prepend(disableZoom)

            disableZoom.addEventListener('touchstart', this.preventDefault)
            disableZoom.addEventListener('gesturestart', this.preventDefault)
            disableZoom.addEventListener('gesturechange', this.preventDefault)
            disableZoom.addEventListener('gestureend', this.preventDefault)
        }

        let handleViewportInterval = -1
        let handleViewportCountdown = 5
        window.addEventListener('resize', (event) => {
            this.onResize(event)

            if (egtLib.Device.isMobile) {
                clearInterval(handleViewportInterval)
                handleViewportCountdown = 5

                // @ts-ignore
                handleViewportInterval = setInterval(() => {
                    this.onResize(event)
                    if (--handleViewportCountdown == 0) {
                        clearInterval(handleViewportInterval)
                    }
                }, 100)
            }
        })
        this.onResize()

        if (egtLib.Device.isMobile) {
            this.onOrientationChange()
            window.addEventListener('orientationchange', () => {
                this.onOrientationChange()
            })
        }

        document.addEventListener(
            'visibilitychange',
            (e) => {
                if (document.visibilityState === 'visible') {
                    window.dispatchEvent(new Event('orientationchange'))
                }
            },
            false
        )
    }

    get usedSafePadding(): number {
        return this._usedSafePadding
    }

    get stage(): PIXI.display.Stage | PIXI.Container {
        return this._stage
    }

    get stageWidth(): number {
        return this._options.stageWidth
    }

    get stageHeight(): number {
        return this._options.stageHeight
    }

    protected getWindowSize() {
        // TODO: make sure the resizing works on iOS apps and homescreen apps
        // if ((window.navigator as any).standalone) {
        //     this._windowWidth = this._window.width();
        //     this._windowHeight = this._window.height();
        // } else {
        this._windowWidth = window.innerWidth
        this._windowHeight = window.innerHeight
        // }
    }

    protected onResize(event?): void {
        this.getWindowSize()

        this.visible_aspect.constrain(this._windowWidth, this._windowHeight)
        this.stage_aspect.crop(this.visible_aspect.width, this.visible_aspect.height)
        const scale = this.stage_aspect.width / this._options.stageWidth
        this._content.style.transform = 'scale(' + scale + ')'
        this.egtLib.Device.scale = scale

        var marginTop = this.egtLib.Device.isMobile ? 0 : (-this.stage_aspect.height + this._windowHeight) / 2

        this._content.style.marginTop = marginTop + 'px'
        this._content.style.marginLeft = (-this.stage_aspect.width + this._windowWidth) / 2 + 'px'

        const stageAspectX = Math.abs(this.stage_aspect.x)
        let usedSafePaddingPercentage = 0
        if (stageAspectX != 0) {
            usedSafePaddingPercentage = 1 - Math.min(this.visible_aspect.x / stageAspectX, 1)
        }

        const resolutionIndependentUsedSafePadding = (this.stage_aspect.width - this.visible_aspect.width) / scale / 2
        this._usedSafePadding =
            (resolutionIndependentUsedSafePadding * usedSafePaddingPercentage) / this._renderer.resolution

        window.postMessage(
            {
                id: 'EventForGame',
                data: {
                    command: 'usedSafePaddingChanged',
                    usedSafePadding: this._usedSafePadding,
                },
            },
            '*'
        )
    }

    protected onOrientationChange() {
        const isTallDeviceGame = window['isTallDeviceGame'] && this._isTallDevice

        let canvasWidth = -1
        let canvasHeight = -1
        let stageWidth = -1
        let stageHeight = -1

        if (this.egtLib.MobileView.deviceOrientation == this.egtLib.DeviceOrientation.Landscape) {
            canvasWidth = this._options.canvasWidth
            canvasHeight = this._options.canvasHeight
            stageWidth = this._options.originalStageWidth
            stageHeight = this._options.originalStageHeight
        } else {
            canvasWidth = this._options.canvasHeight
            canvasHeight = this._options.canvasWidth
            stageWidth = this._options.originalStageHeight
            stageHeight = this._options.originalStageWidth

            if (isTallDeviceGame) {
                stageHeight -= Stage.TALL_DEVICE_BOTTOM_OFFSET
                canvasHeight -= Stage.TALL_DEVICE_BOTTOM_OFFSET
            }
        }

        this.overrideAspect(canvasWidth, canvasHeight, stageWidth, stageHeight)
    }

    protected overrideAspect(
        canvasWidth: number,
        canvasHeight: number,
        stageWidth: number,
        stageHeight: number,
        visibleWidth?: number,
        visibleHeight?: number
    ) {
        if (isNaN(visibleWidth)) visibleWidth = stageWidth
        if (isNaN(visibleHeight)) visibleHeight = stageHeight

        const isTallDeviceGame = window['isTallDeviceGame'] && this._isTallDevice

        if (this._additionalHeightOnSmallDevices && this.isIOS()) {
            stageHeight += this._additionalHeightOnSmallDevices
            visibleHeight += this._additionalHeightOnSmallDevices
        }

        this.stage_aspect = new Aspect(stageWidth, stageHeight)
        this.visible_aspect = new Aspect(visibleWidth, visibleHeight)

        this._options.stageWidth = stageWidth
        this._options.stageHeight = stageHeight

        this._renderer.resize(canvasWidth, canvasHeight)

        this._content.style.width = stageWidth + 'px'
        this._content.style.height = stageHeight + 'px'

        this.onResize()
    }

    protected preventDefault(event) {
        event.preventDefault()
        event.stopPropagation()
    }

    protected animate(): void {
        this._renderer.render(this._stage)
        requestAnimationFrame(() => {
            this.animate()
        })
    }

    // TODO: use it from @egt/egt-library maybe?
    protected isIOS(): boolean {
        return (
            navigator.userAgent.match(/iPhone/i) != null ||
            navigator.userAgent.match(/iPad/i) != null ||
            navigator.userAgent.match(/iPod/i) != null ||
            // since iPad is not present in the userAgent for iPad OS 13 an alternative was needed
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        )
    }
}
